import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Zap, Settings, Hash, Tag, CheckCircle2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { FlowNodeProps } from '@/types/flow';

const TriggerNode = ({ data, selected }: FlowNodeProps) => {
    const triggers = data.config?.triggers || [];

    const getTriggerIcon = (type: string) => {
        switch (type) {
            case 'keyword': return <Hash className="w-3 h-3" />;
            case 'tag': return <Tag className="w-3 h-3" />;
            case 'exact_match': return <CheckCircle2 className="w-3 h-3" />;
            default: return <Hash className="w-3 h-3" />;
        }
    };

    return (
        <div
            className={`
                bg-white rounded-lg border-l-[3px] border-l-green-500 shadow-sm min-w-[260px] max-w-[300px]
                transition-all duration-150
                ${selected ? 'shadow-md ring-2 ring-blue-400/40' : 'border-t border-r border-b border-gray-200 hover:shadow-md'}
            `}
            style={{ borderTop: selected ? undefined : '1px solid #e5e7eb', borderRight: selected ? undefined : '1px solid #e5e7eb', borderBottom: selected ? undefined : '1px solid #e5e7eb' }}
        >
            {/* Header */}
            <div className="px-3 py-2.5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-green-50 rounded-lg flex items-center justify-center">
                        <Zap className="w-3.5 h-3.5 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <span className="font-semibold text-[13px] text-gray-800 block truncate">{data.node_name || 'Gatilho'}</span>
                        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Passo Inicial</span>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-3 py-2.5">
                {triggers.length === 0 ? (
                    <div className="text-center py-3">
                        <p className="text-xs text-gray-400">Clique para configurar gatilhos</p>
                    </div>
                ) : (
                    <div className="flex flex-wrap gap-1.5">
                        {triggers.slice(0, 5).map((trigger, idx) => (
                            <div
                                key={idx}
                                className="inline-flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-md px-2 py-1"
                            >
                                {getTriggerIcon(trigger.trigger_type)}
                                <span className="text-[11px] text-gray-600 font-medium max-w-[100px] truncate">
                                    {trigger.trigger_value}
                                </span>
                            </div>
                        ))}
                        {triggers.length > 5 && (
                            <span className="text-[11px] text-gray-400 self-center ml-1">+{triggers.length - 5}</span>
                        )}
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="px-3 py-1.5 border-t border-gray-100 flex gap-1">
                <Button
                    size="sm"
                    variant="ghost"
                    className="flex-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50/50 h-7 text-[11px]"
                    onClick={() => data.onEdit?.(data.id)}
                >
                    <Settings className="w-3 h-3 mr-1" />
                    Configurar
                </Button>
                {/* Só aparece quando o funil tem mais de um passo inicial (duplicação/import) */}
                {data.canDelete && (
                    <Button
                        size="sm"
                        variant="ghost"
                        className="text-gray-400 hover:text-red-500 hover:bg-red-50/50 h-7 px-2"
                        title="Excluir passo inicial duplicado"
                        onClick={(e) => { e.stopPropagation(); data.onDelete?.(data.id); }}
                    >
                        <Trash2 className="w-3 h-3" />
                    </Button>
                )}
            </div>

            <Handle
                type="source"
                position={Position.Right}
                id="source"
                className="!w-3.5 !h-3.5 !bg-[#2196F3] !border-2 !border-white"
                style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
            />
        </div>
    );
};

export default memo(TriggerNode);
